// The group page's Network card (D81): allowed and blocked requests in the chosen period, the
// list the group uses and whether it shares the VM's network; it opens Task group › Network.
import { ArrowRight, GlobeHemisphereWest, ProhibitInset, ShieldCheck } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { blockedTone } from "../tasks/group/summaries";
import { formatCompact, formatNumber, useT } from "../i18n";
import { CollapsibleCard } from "../ui/CollapsibleCard";
import { Segmented } from "../ui/Segmented";
import { ErrorState, Loading } from "../ui/states";
import { Chip, SummaryRow } from "../ui/summary";
import { groupNetworkQuery } from "./api";
import { DAY_CHOICES, type Days, useDays } from "./days";

function Count({ tone, label, value }: { tone: "green" | "red"; label: string; value: string }) {
  const Icon = tone === "green" ? ShieldCheck : ProhibitInset;
  return (
    <div className="rounded-lg border border-muted p-3">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-gray-500">
        <Icon weight="duotone" className={tone === "green" ? "h-4 w-4 text-green" : "h-4 w-4 text-red"} aria-hidden /> {label}
      </div>
      <div className="mt-1 truncate font-display text-xl font-bold tabular-nums text-gray-900">{value}</div>
    </div>
  );
}

function Summary({ allowed, blocked, days }: { allowed: number; blocked: number; days: Days }) {
  const c = useT().tasks.cards.network;
  return (
    <SummaryRow>
      <Chip tone="green" priority="optional" icon={<ShieldCheck weight="bold" aria-hidden />} title={c.allowed(allowed, days)}>
        {formatCompact(allowed)}
      </Chip>
      <Chip tone={blockedTone(blocked)} icon={<ProhibitInset weight="bold" aria-hidden />} title={c.blocked(blocked, days)}>
        {formatCompact(blocked)}
      </Chip>
      <Chip priority="extra">{c.window(days)}</Chip>
    </SummaryRow>
  );
}

export function GroupNetworkCard({ groupId }: { groupId: string }) {
  const t = useT().network.group;
  const [days, setDays] = useDays();
  const network = useQuery(groupNetworkQuery(groupId, days));
  const n = network.data;
  return (
    <CollapsibleCard
      kind="network"
      icon={<GlobeHemisphereWest weight="duotone" />}
      title={t.title}
      description={t.description}
      summary={n ? <Summary allowed={n.allowed} blocked={n.blocked} days={days} /> : null}
    >
      <div className="space-y-4">
        <Segmented label={t.window} options={DAY_CHOICES.map((d) => ({ key: d, label: t.days(d) }))} value={days} onChange={setDays} />
        {network.isPending ? <Loading /> : null}
        {network.isError ? <ErrorState title={t.loadFailed} message={network.error.message} /> : null}
        {n ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Count tone="green" label={t.allowed} value={formatNumber(n.allowed)} />
              <Count tone="red" label={t.blocked} value={formatNumber(n.blocked)} />
            </div>
            <dl className="m-0 space-y-1 text-[13px]">
              <div className="flex flex-wrap gap-x-2">
                <dt className="text-gray-500">{t.list}:</dt>
                <dd className="m-0 font-medium">
                  {n.network_list ? (
                    <Link to={`/network/${n.network_list.id}`} className="text-primary hover:underline">
                      {n.network_list.name}
                    </Link>
                  ) : (
                    t.defaults
                  )}
                </dd>
              </div>
              {n.share_vm_network ? <div className="text-gray-700">{t.sharesVm}</div> : null}
            </dl>
            <Link to={`/task-groups/${groupId}/network`} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
              {t.open} <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </>
        ) : null}
      </div>
    </CollapsibleCard>
  );
}
